/* ============================================================================
 * Boot — CRT power-on sequence. Cosmetic only; it never gates functionality.
 * ----------------------------------------------------------------------------
 * Laid out as the design source's boot plate (supercompute-skin.css →
 * `.boot__plate`): the 64px company mark, the SUPERCOMPUTE wordmark in
 * Phosphate, the `supercompute.eth` handle tracked out in brass, then the
 * routine. The logo is the real asset from /logos — one mark, sized per
 * surface, never redrawn.
 * ========================================================================= */

import { useEffect, useState } from 'react';
import { useAppearance } from './AppearanceProvider';
import { skinDef } from '../hooks/useSkin';

const LINES = [
  'POST ............... ok',
  'chassis ............ oceanside nude build ... mounted',
  'wagmi transport .... base(8453) mainnet(1) ... ready',
  'eas registry ....... 0x4200…0020 ... reachable',
  'presence verifier .. 0xF095…F6 ... eip-712 armed',
  'self-custody ....... keys never leave the browser ... ok',
];

const SKIP_KEY = 'supercompute-webos:booted';
const BOOT_MS = 170;

export function Boot({ onDone }: { onDone: () => void }) {
  const [shown, setShown] = useState<number>(() =>
    sessionStorage.getItem(SKIP_KEY) === '1' ? LINES.length : 0,
  );

  useEffect(() => {
    if (shown >= LINES.length) {
      sessionStorage.setItem(SKIP_KEY, '1');
      const t = window.setTimeout(onDone, 420);
      return () => window.clearTimeout(t);
    }
    const t = window.setTimeout(() => setShown((n) => n + 1), BOOT_MS);
    return () => window.clearTimeout(t);
  }, [shown, onDone]);

  // Branding follows the active skin. An Immersion boots wearing its OWN mark,
  // wordmark and handle — that is the Franchisee's charter: they own the
  // community's culture, so they own what the load screen says. Hardcoding
  // Supercompute here meant a School or Club Immersion booted as somebody else.
  const { skin } = useAppearance();
  const brand = skinDef(skin);

  const pct = Math.round((Math.min(shown, LINES.length) / LINES.length) * 100);

  return (
    <div className="boot wos-crt" onClick={onDone} role="presentation">
      <div className="boot__plate">
        <img
          className="brandmark pix"
          src={brand.mark}
          width={64}
          height={64}
          alt=""
        />
        <h1 className="boot__mark">{brand.wordmark}</h1>
        <p className="boot__ens">{brand.handle.toUpperCase()}</p>
        {brand.tagline && <p className="boot__tag">{brand.tagline}</p>}

        <div className="boot__band" aria-hidden="true" />

        <div
          className="boot__bar"
          role="progressbar"
          aria-label="Boot"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <i style={{ width: `${pct}%` }} />
        </div>

        <div className="boot__log">
          {LINES.slice(0, shown).map((l, i) => (
            <div
              key={l}
              className={'boot__line' + (i >= 1 ? ' boot__line--ok' : '')}
            >
              {l}
            </div>
          ))}
          <div className="boot__line">
            {shown >= LINES.length ? 'launching desktop' : 'booting'}
            <span className="boot__cursor" />
          </div>
        </div>

        <div className="boot__foot">
          <span>sovereign webos 0.1.0</span>
          <span>click to skip</span>
        </div>
      </div>
    </div>
  );
}
